import { useState, useMemo } from 'react';
import { Check, X, Edit, Trash2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Role, Permission } from '@/types/user';
import {
  usePermissionMatrix,
  useAssignRolePermissions,
} from '@/hooks/react-query/useRoles';

interface RolesPermissionsProps {
  roles: Role[];
}

export function RolesPermissions({ roles }: RolesPermissionsProps) {
  const [savingPermissions, setSavingPermissions] = useState<Set<string>>(
    () => new Set()
  );
  const [permissionOverrides, setPermissionOverrides] = useState<
    Map<string, Permission[]>
  >(() => new Map());

  const { data: permissionMatrix, isLoading: loading } = usePermissionMatrix();
  const assignRolePermissionsMutation = useAssignRolePermissions();

  const effectivePermissions = (role: Role) =>
    permissionOverrides.get(role.id) ?? role.permissions ?? [];

  const hasPermission = (role: Role, permission: Permission): boolean =>
    effectivePermissions(role).some((p) => p.id === permission.id);

  const togglePermission = async (role: Role, permission: Permission) => {
    if (role.name === 'super_admin') return;

    const currentPerms = effectivePermissions(role);
    const currentHasPermission = currentPerms.some(
      (p) => p.id === permission.id
    );
    const savingKey = `${role.id}-${permission.id}`;

    const optimisticPerms = currentHasPermission
      ? currentPerms.filter((p) => p.id !== permission.id)
      : [...currentPerms, permission];

    setPermissionOverrides((prev) =>
      new Map(prev).set(role.id, optimisticPerms)
    );
    setSavingPermissions((prev) => new Set(prev).add(savingKey));

    try {
      await assignRolePermissionsMutation.mutateAsync({
        id: role.id,
        permissionIds: optimisticPerms.map((p) => p.id),
      });

      setPermissionOverrides((prev) => {
        const next = new Map(prev);
        next.delete(role.id);
        return next;
      });
    } catch {
      setPermissionOverrides((prev) =>
        new Map(prev).set(role.id, currentPerms)
      );
    } finally {
      setSavingPermissions((prev) => {
        const next = new Set(prev);
        next.delete(savingKey);
        return next;
      });
    }
  };

  const groupedPermissions = useMemo(
    () =>
      permissionMatrix?.permissions.reduce(
        (acc: Record<string, Permission[]>, permission: Permission) => {
          if (!acc[permission.module]) acc[permission.module] = [];
          acc[permission.module].push(permission);
          return acc;
        },
        {} as Record<string, Permission[]>
      ) || {},
    [permissionMatrix?.permissions]
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Roles Overview */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Roles</CardTitle>
            <CardDescription>
              View user roles and their access permissions within the platform.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {roles
              .filter((role) => role.name !== 'super_admin')
              .map((role) => (
                <Card key={role.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full shrink-0"
                        style={{ backgroundColor: role.color }}
                      />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {role.display_name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {role.description}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className="text-xs text-gray-500">
                            {effectivePermissions(role).length} permissions
                          </span>
                          {role.is_system && (
                            <Badge variant="secondary" className="text-xs">
                              System
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {!role.is_system && (
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Permission Matrix */}
      <Card>
        <CardHeader>
          <CardTitle>Permission Matrix</CardTitle>
          <CardDescription>
            View and manage which roles have access to specific platform
            features. Click the checkboxes to toggle permissions for roles. The
            superadmin role (marked as read-only) cannot be modified.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {Object.entries(groupedPermissions).map(
              ([module, permissions]: [string, Permission[]]) => (
                <div key={module} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">
                    {module.replace('_', ' ')} Management
                  </h3>

                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[300px]">
                            Permission
                          </TableHead>
                          {roles.map((role) => (
                            <TableHead
                              key={role.id}
                              className="text-center min-w-[120px]"
                            >
                              <div className="flex items-center justify-center space-x-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: role.color }}
                                />
                                <div className="flex flex-col items-center">
                                  <span className="text-sm font-medium">
                                    {role.display_name}
                                  </span>
                                  {role.name === 'super_admin' && (
                                    <span className="text-xs text-gray-500 mt-0.5">
                                      Read-only
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {permissions.map((permission) => (
                          <TableRow key={permission.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {permission.display_name}
                                </div>
                                {permission.description && (
                                  <div className="text-sm text-gray-500">
                                    {permission.description}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            {roles.map((role) => {
                              const isChecked = hasPermission(role, permission);
                              const savingKey = `${role.id}-${permission.id}`;
                              const isSaving = savingPermissions.has(savingKey);
                              const isReadOnly = role.name === 'super_admin';

                              return (
                                <TableCell
                                  key={`${role.id}-${permission.id}`}
                                  className="text-center"
                                >
                                  <div className="flex justify-center">
                                    {isSaving ? (
                                      <div className="w-6 h-6 flex items-center justify-center">
                                        <Loader2 className="h-4 w-4 animate-spin text-slate-700" />
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          togglePermission(role, permission)
                                        }
                                        disabled={isReadOnly}
                                        className={[
                                          'w-6 h-6 rounded-full flex items-center justify-center transition-colors',
                                          isReadOnly
                                            ? 'cursor-not-allowed opacity-60'
                                            : 'cursor-pointer hover:scale-110',
                                          isChecked
                                            ? 'bg-green-100'
                                            : 'bg-gray-100',
                                          !isReadOnly && isChecked
                                            ? 'hover:bg-green-200'
                                            : '',
                                          !isReadOnly && !isChecked
                                            ? 'hover:bg-gray-200'
                                            : '',
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                        title={
                                          isReadOnly
                                            ? 'Superadmin role cannot be modified'
                                            : `Toggle ${permission.display_name} for ${role.display_name}`
                                        }
                                      >
                                        {isChecked ? (
                                          <Check className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <X className="h-4 w-4 text-gray-400" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
